import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useListAssets } from '@/features/assets/api/list-assets';
import { Check, Film, ImageIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  ComposerDecision,
  useCurrentComposerTurn,
} from '../composer-gate-context';
import { CompactPhotoPicker } from './compact-photo-picker';
import { DecisionFooter } from './decision-footer';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { VideoThumbnail } from '@/routes/_authed/create-video/$templateId/-components/shared/video-thumbnail';

interface AssetItem {
  id: string;
  name: string;
  type?: string;
  duration?: number | string | null;
  blobUrl?: string | null;
  thumbnailUrl?: string | null;
  tags?: string[] | null;
}

interface ClipSelectionGridProps {
  id?: string;
  total?: number;
  expanded?: boolean;
  requiresSelection?: boolean;
  onCancel?: () => void;
  assets: AssetItem[];
  /** Seeds the selection, e.g. reopening the browser on the current choice. */
  initialSelectedIds?: string[];
  /** Called when the user confirms their selection */
  onConfirm: (selectedIds: string[]) => void;
}

function formatDuration(
  duration: number | string | null | undefined
): string | null {
  if (duration == null) return null;
  const seconds =
    typeof duration === 'string' ? Number.parseFloat(duration) : duration;
  if (Number.isNaN(seconds)) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function ClipSelectionGrid({
  id,
  total,
  expanded = false,
  requiresSelection = true,
  onCancel,
  assets,
  initialSelectedIds,
  onConfirm,
}: ClipSelectionGridProps) {
  const currentTurn = useCurrentComposerTurn();
  const [browserOpen, setBrowserOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(
    () =>
      new Set(
        initialSelectedIds ??
          (requiresSelection
            ? assets
                .filter((asset) => asset.type === 'video')
                .map((asset) => asset.id)
            : [])
      )
  );
  // Clips picked from a library page past the tool snapshot, so their
  // thumbnails survive closing the browser.
  const [librarySelection, setLibrarySelection] = useState<AssetItem[]>([]);

  const [resolved, setResolved] = useState(false);
  const accept = () => {
    if (resolved || selected.size === 0) return;
    setResolved(true);
    onConfirm(Array.from(selected));
  };
  const reject = () => {
    if (resolved) return;
    setResolved(true);
    onCancel?.();
  };
  const mediaLabel = assets.every((asset) => asset.type === 'image')
    ? 'photos'
    : assets.every((asset) => asset.type === 'video')
      ? 'clips'
      : 'items';
  const gate =
    !expanded && !resolved && assets.length > 0
      ? {
          sentence: `Choose ${mediaLabel}`,
          onAccept: accept,
          onReject: reject,
          acceptLabel: 'Use selected',
          onEdit: () => setBrowserOpen(true),
          editLabel: `Choose ${mediaLabel}`,
          acceptDisabled: selected.size === 0,
          acceptDisabledReason: `Choose at least one ${mediaLabel === 'photos' ? 'photo' : mediaLabel === 'clips' ? 'clip' : 'item'} first.`,
        }
      : null;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (assets.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        No media available.
      </div>
    );
  }

  if (resolved || (!requiresSelection && !currentTurn)) return null;

  if (!requiresSelection && !expanded && mediaLabel !== 'photos') {
    const known = [
      ...new Map(
        [...assets, ...librarySelection].map((asset) => [asset.id, asset])
      ).values(),
    ];
    const knownById = new Map(known.map((asset) => [asset.id, asset]));
    // The card previews the selection, in the order it was made — never the
    // first library clips, which are not what Use selected will send.
    const selectedAssets = Array.from(selected).flatMap((assetId) => {
      const asset = knownById.get(assetId);
      return asset ? [asset] : [];
    });
    const itemLabel = mediaLabel === 'clips' ? 'clip' : 'item';
    // Browsing clips is a decision like browsing photos, so it lives in the
    // composer too. It used to render inline in the transcript with a dialog
    // that sent the selection on the spot, while the same browse for photos
    // replaced the composer and waited for Use selected.
    return (
      <ComposerDecision id={id ?? 'asset-picker'} gate={gate}>
        <div className="w-full space-y-2 rounded-lg border bg-card p-3 sm:max-w-md">
          <p className="text-sm font-medium">
            Choose {mediaLabel} ({selected.size} selected)
          </p>
          {selectedAssets.length > 0 && (
            <div className="flex gap-2">
              {selectedAssets
                .slice(0, selectedAssets.length > 4 ? 3 : 4)
                .map((asset, index) => (
                  <button
                    type="button"
                    key={asset.id}
                    aria-label={`Preview ${itemLabel} ${index + 1}`}
                    onClick={() => setBrowserOpen(true)}
                    className="grid size-14 shrink-0 place-items-center overflow-hidden rounded-md border bg-muted"
                  >
                    {asset.thumbnailUrl ? (
                      <img
                        src={asset.thumbnailUrl}
                        alt=""
                        className="size-full object-cover"
                      />
                    ) : asset.blobUrl ? (
                      <VideoThumbnail
                        src={asset.blobUrl}
                        className="size-full"
                        isImage={asset.type === 'image'}
                      />
                    ) : (
                      <Film className="size-5 text-muted-foreground" />
                    )}
                  </button>
                ))}
              {selectedAssets.length > 4 && (
                <button
                  type="button"
                  aria-label={`View ${selectedAssets.length - 3} more selected ${mediaLabel}`}
                  onClick={() => setBrowserOpen(true)}
                  className="grid size-14 place-items-center rounded-md border bg-muted text-sm"
                >
                  +{selectedAssets.length - 3}
                </button>
              )}
            </div>
          )}
          {/* The card answers itself too, for surfaces with no composer. */}
          <DecisionFooter
            hideSentence
            sentence={`Choose ${mediaLabel}`}
            onEdit={() => setBrowserOpen(true)}
            editLabel={`Choose ${mediaLabel}`}
            onAccept={accept}
            onReject={reject}
            acceptLabel={`Use selected (${selected.size})`}
            rejectLabel="Cancel"
            acceptDisabled={selected.size === 0}
            acceptDisabledReason={`Choose at least one ${itemLabel} first.`}
          />
          <Dialog open={browserOpen} onOpenChange={setBrowserOpen}>
            <DialogContent className="flex max-h-[85dvh] flex-col overflow-hidden">
              <DialogHeader>
                <DialogTitle>Choose {mediaLabel}</DialogTitle>
              </DialogHeader>
              <ClipLibraryBrowser
                type={mediaLabel === 'clips' ? 'video' : undefined}
                assets={known}
                total={total}
                selectedIds={Array.from(selected)}
                onConfirm={(selection) => {
                  if (resolved || !selection.length) return;
                  setLibrarySelection(selection);
                  setSelected(new Set(selection.map((asset) => asset.id)));
                  setBrowserOpen(false);
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </ComposerDecision>
    );
  }

  return (
    <ComposerDecision id={id ?? 'asset-picker'} gate={gate}>
      <div
        className={
          expanded
            ? 'flex min-h-0 flex-1 flex-col'
            : 'w-full rounded-lg border bg-card p-4 sm:max-w-lg'
        }
        data-testid="claire-card-asset-picker"
      >
        <div
          className={
            expanded
              ? 'sr-only'
              : 'mb-3 flex items-center gap-2 text-sm font-medium'
          }
        >
          {mediaLabel === 'photos' ? (
            <ImageIcon className="size-4 text-muted-foreground" />
          ) : (
            <Film className="size-4 text-muted-foreground" />
          )}
          <span>
            Choose {mediaLabel} ({selected.size} selected)
          </span>
          {expanded && total && total > assets.length ? (
            <span className="block text-xs font-normal text-muted-foreground">
              Showing {assets.length} of {total}.
            </span>
          ) : null}
        </div>

        {mediaLabel === 'photos' ? (
          <CompactPhotoPicker
            browseLibrary={!requiresSelection}
            assets={assets}
            selectedIds={Array.from(selected)}
            disabled={resolved}
            showTrigger={expanded}
            open={!expanded ? browserOpen : undefined}
            onOpenChange={!expanded ? setBrowserOpen : undefined}
            onConfirm={(ids) => {
              if (resolved || !ids.length) return;
              setSelected(new Set(ids));
            }}
          />
        ) : (
          <div
            className={expanded ? 'min-h-0 flex-1 overflow-y-auto' : undefined}
          >
            <div className="grid grid-cols-2 items-start gap-2 sm:grid-cols-3 md:grid-cols-4">
              {assets.map((asset) => {
                const isSelected = selected.has(asset.id);
                const dur = formatDuration(asset.duration);

                return (
                  <button
                    key={asset.id}
                    type="button"
                    disabled={resolved}
                    aria-label={`Select ${asset.name}`}
                    aria-pressed={isSelected}
                    onClick={() => toggle(asset.id)}
                    className={cn(
                      'relative flex aspect-square w-full shrink-0 items-center justify-center overflow-hidden rounded-md border-2 bg-muted transition-colors',
                      isSelected
                        ? 'border-primary ring-1 ring-primary'
                        : 'border-transparent hover:border-muted-foreground/30'
                    )}
                  >
                    {/* Thumbnail. `blobUrl` is the .mp4 — it can never paint in an
                  <img>, so a clip without a poster frame goes through
                  VideoThumbnail, same as the full clip picker. */}
                    {asset.thumbnailUrl ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={asset.thumbnailUrl}
                        alt={asset.name}
                        className="size-full object-cover"
                      />
                    ) : asset.blobUrl ? (
                      <VideoThumbnail
                        src={asset.blobUrl}
                        className="size-full"
                        isImage={asset.type === 'image'}
                      />
                    ) : (
                      <Film className="size-6 text-muted-foreground" />
                    )}

                    {/* Selection check */}
                    {isSelected && (
                      <div className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="size-3" />
                      </div>
                    )}

                    {/* Duration badge */}
                    {dur && (
                      <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 py-0.5 text-[10px] text-white">
                        {dur}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!expanded && (
          <div className="mt-3">
            <DecisionFooter
              hideSentence
              sentence={`Choose ${mediaLabel}`}
              onAccept={accept}
              onReject={reject}
              onEdit={() => setBrowserOpen(true)}
              editLabel={`Choose ${mediaLabel}`}
              acceptLabel={`Use selected (${selected.size})`}
              rejectLabel="Cancel"
              acceptDisabled={selected.size === 0}
              acceptDisabledReason={`Choose at least one ${mediaLabel === 'photos' ? 'photo' : mediaLabel === 'clips' ? 'clip' : 'item'} first.`}
            />
          </div>
        )}
        {!requiresSelection && expanded && mediaLabel !== 'photos' && (
          <Button
            size="sm"
            className="mt-3 w-full shrink-0"
            disabled={resolved || selected.size === 0}
            onClick={accept}
          >
            Use selected ({selected.size})
          </Button>
        )}
      </div>
    </ComposerDecision>
  );
}

const LIBRARY_PAGE_SIZE = 100;

/** Keep the editor mounted while pages append so selected clips are retained. */
function ClipLibraryBrowser({
  assets: initialAssets,
  total: initialTotal,
  type,
  selectedIds,
  onConfirm,
}: {
  assets: AssetItem[];
  total?: number;
  type?: 'video';
  selectedIds: string[];
  onConfirm: (selection: AssetItem[]) => void;
}) {
  const [offset, setOffset] = useState(0);
  const [loaded, setLoaded] = useState(initialAssets);
  const { assets, total, isLoading, isError, refetch } = useListAssets({
    type,
    source: 'raw',
    limit: LIBRARY_PAGE_SIZE,
    offset,
  });
  useEffect(() => {
    if (!assets.length) return;
    setLoaded((previous) => [
      ...new Map(
        [...previous, ...assets].map((asset) => [asset.id, asset])
      ).values(),
    ]);
  }, [assets]);
  const count = total || initialTotal || loaded.length;
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {loaded.length} of {count} loaded
        </span>
        {isError ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void refetch();
            }}
          >
            Retry media
          </Button>
        ) : (
          // Pages fetched so far, not loaded.length: clips carried over from
          // a later page would otherwise hide Load more early.
          offset + LIBRARY_PAGE_SIZE < count && (
            <Button
              size="sm"
              variant="outline"
              disabled={isLoading}
              onClick={() => setOffset(offset + LIBRARY_PAGE_SIZE)}
            >
              {isLoading ? 'Loading…' : 'Load more'}
            </Button>
          )
        )}
      </div>
      <ClipSelectionGrid
        expanded
        requiresSelection={false}
        assets={loaded}
        initialSelectedIds={selectedIds}
        onConfirm={(ids) => {
          const byId = new Map(loaded.map((asset) => [asset.id, asset]));
          onConfirm(ids.flatMap((assetId) => byId.get(assetId) ?? []));
        }}
      />
    </>
  );
}
